import { Entity } from '@toggle/toggle';
import React from 'react';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { BrandAndProductDrawer } from '~/modules/brand-and-product-drawer/BrandAndProductDrawer';
import { useCompanyCompetitors } from '~/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { NewsView } from '~/views/news/components/news-view/NewsView';
import { AssetFinancialsWidget } from '~/widgets/asset-financials-widget/AssetFinancialsWidget';
import { ChartPeerGroup } from '~/widgets/chart-peer-group/ChartPeerGroup';
import { Document } from '~/widgets/document-widget/DocumentWidget';
import { Earnings } from '~/widgets/earnings/Earnings';
import { CompetitorsDrawer } from '~/widgets/knowledge-graph-widget/components/competitors-tab/competitors-drawer/CompetitorsDrawer';
import { GeographicExposureDrawer } from '~/widgets/knowledge-graph-widget/components/geographic-exposure-tab/drawers/GeographicExposureDrawer';
import { KnowledgeGraphWidget } from '~/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import { Overview } from '~/widgets/overview/Overview';
import { PEER_GROUP_DISPLAYED } from '~/widgets/overview/utils';
import { ThemeDrawer } from '~/widgets/theme-widget/theme-drawer/ThemeDrawer';

import { TabsType } from './types';

export interface AssetOverviewTabsProps {
  entity: Entity;
  activeTab: TabsType;
}

export const TabContent = ({ entity, activeTab }: AssetOverviewTabsProps) => {
  const { competitorsData, isFetching } = useCompanyCompetitors({
    entityTag: entity.tag,
    limit: PEER_GROUP_DISPLAYED,
  });
  const peerEntities = competitorsData.map(s => s.entity);

  const renderElement = () => {
    switch (activeTab) {
      case TabsType.Financials: {
        return <AssetFinancialsWidget entity={entity} />;
      }

      case TabsType.Chart: {
        return (
          <ChartPeerGroup
            entity={entity}
            peerEntities={peerEntities}
            isFetching={isFetching}
          />
        );
      }

      case TabsType.Connections: {
        return <KnowledgeGraphWidget entity={entity} />;
      }

      case TabsType.NewsInsights: {
        return (
          <NewsView
            entity={entity}
            includedFilterKeys={['providers']}
            trackingSource={POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW}
          />
        );
      }

      case TabsType.Documents: {
        return (
          <ErrorBoundaryWrapper showBorder={false}>
            <Document entityTag={entity.tag} />
          </ErrorBoundaryWrapper>
        );
      }

      case TabsType.Earnings: {
        return <Earnings entity={entity} />;
      }

      case TabsType.Overview:

      default: {
        return <Overview entity={entity} key={entity.tag} />;
      }
    }
  };

  return (
    <>
      <CompetitorsDrawer entity={entity} />
      <ThemeDrawer entity={entity} />
      <GeographicExposureDrawer entity={entity} />
      <BrandAndProductDrawer entity={entity} />
      {renderElement()}
    </>
  );
};
